// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import { color, getSystemColor, xMask, xValidation } from "@keller/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    phoneNumber = "phoneNumber",
    email = "email",
    businessDays = "businessDays",
    companyName = "companyName",
    street = "street",
    directionLink = "directionLink",
}

interface OwnProps {
    fields: {
        [fields.phoneNumber]: string;
        [fields.email]: string;
        [fields.businessDays]: string;
        [fields.companyName]: string;
        [fields.street]: string;
        [fields.directionLink]: LinkFieldValue;
    };
}

type Props = OwnProps;

interface FooterContactUsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    phoneNumber?: TypographyPresentationProps;
    email?: TypographyPresentationProps;
    companyName?: TypographyPresentationProps;
    street?: TypographyPresentationProps;
    businessDays?: InjectableCss;
    getDirections?: LinkPresentationProps;
}

const hoverLink = css`
    &:hover {
        color: ${color("common.ui5")};
`;

const footerContactUsStyles: FooterContactUsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 198px;
        `,
    },
    title: {
        size: 12,
        weight: "bold",
        color: "common.white",
        transform: "uppercase",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            padding-top: 12px;
            margin-bottom: 16px;
            position: relative;
            &:after {
                content: " ";
                position: absolute;
                top: 0;
                left: 0;
                width: 126px;
                height: 2px;
                background: ${color("common.ui11")};
            }
        `,
    },
    phoneNumber: {
        size: 12,
        weight: "bold",
        color: "common.white",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 14px;
            display: inline-block;
            ${hoverLink}
        `,
    },
    email: {
        size: 12,
        weight: "bold",
        color: "common.white",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 28px;
            display: inline-block;
            ${hoverLink}
        `,
    },
    businessDays: {
        css: css`
            margin-bottom: 16px;
            ${TypographyStyle} {
                font-size: 12px;
                font-weight: normal;
                letter-spacing: 0;
                line-height: 15px;
                color: ${color("common.white", 0.66)};
                margin-bottom: 0;
            }
        `,
    },
    companyName: {
        size: 12,
        weight: "bold",
        color: "common.white",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 16px;
        `,
    },
    street: {
        css: css`
            margin-bottom: 16px;
            ${TypographyStyle} {
                font-size: 12px;
                font-weight: normal;
                letter-spacing: 0;
                line-height: 15px;
                color: ${color("common.white", 0.66)};
                margin-bottom: 0;
            }
        `,
    },
    getDirections: {
        color: "common.white",
        hoverStyle: {
            color: getSystemColor("common.ui5"),
        },
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                display: inline-block;
            `,
        },
    },
};

const styles = footerContactUsStyles;
const FooterContactUs: FC<Props> = ({
    fields: { phoneNumber, email, businessDays, companyName, street, directionLink: directionLinkProps },
}) => {
    const directionLink = useGetLink(directionLinkProps);
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{translate("Contact Us")}</Typography>
            {phoneNumber && (
                <StyledWrapper>
                    <ContactTypography type="phone" targetValue={phoneNumber} {...styles.phoneNumber}>
                        {xMask.phoneNumber(phoneNumber)}
                    </ContactTypography>
                </StyledWrapper>
            )}
            {businessDays && (
                <StyledWrapper {...styles.businessDays}>{parse(businessDays, parserOptions)}</StyledWrapper>
            )}
            {email && (
                <StyledWrapper>
                    <ContactTypography type="email" targetValue={email} {...styles.email}>
                        {email}
                    </ContactTypography>
                </StyledWrapper>
            )}
            {companyName && <Typography {...styles.companyName}>{companyName}</Typography>}
            {street && <StyledWrapper {...styles.street}>{parse(street, parserOptions)}</StyledWrapper>}
            {directionLink.url && (
                <StyledWrapper>
                    <Link href={directionLink.url} {...styles.getDirections}>
                        {translate("Get Directions")}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterContactUs,
    definition: {
        displayName: "Footer - Contact Us",
        group: "Footer",
        fieldDefinitions: [
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return xValidation.phone(value)
                        ? null
                        : siteMessage("Field_Invalid", translate("Phone Number")).toString();
                },
            },
            {
                name: fields.businessDays,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return xValidation.email(value)
                        ? null
                        : siteMessage("Field_Invalid", translate("Email")).toString();
                },
            },
            {
                name: fields.companyName,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.street,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.directionLink,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
